/**
 * Date Calculation Utilities
 *
 * This file contains utility functions for performing mathematical operations on dates.
 * These functions CREATE NEW dates by performing arithmetic, adjustments, or computations.
 * Use these utilities when you need to:
 * - Add or subtract time periods to create new dates (e.g., date + 3 days = new date)
 * - Calculate differences between dates (e.g., duration between start and end)
 * - Compute age from a birth date (e.g., years, months, days old)
 * - Adjust dates to specific values (e.g., change hours to 14:00, set day to 15)
 * - Sort arrays of dates or timestamps in ascending/descending order
 * - Perform any math that results in a NEW date or duration value
 *
 * Key distinction: These functions return NEW dates or computed values, not properties of existing dates.
 */
import {
  DateTime as LuxonDateTime,
  type DateObjectUnits as LuxonDateObjectUnits,
  type DurationObjectUnits as LuxonDurationObjectUnits,
} from 'luxon'
import { isDateValid } from '@/utils/date/validation.js'
import { getDateTime } from '@/utils/date/getter.js'

/**
 * Adds a specified amount of time (months, days, hours) to a date and returns a new Date object.
 *
 * @param date - The starting date as a Date object or ISO string
 * @param amount - An object specifying the units to add (months, days, hours)
 * @returns A new JavaScript Date object with the time added
 *
 * @example
 * ```typescript
 * addToDate('2024-01-15', { days: 7 })
 * // Returns: Date object representing 2024-01-22
 *
 * addToDate(new Date('2024-01-15'), { months: 2, days: 10 })
 * // Returns: Date object representing 2024-03-25
 * ```
 */
export function addToDate(
  date: Date | string,
  amount: {
    months?: number
    weeks?: number
    days?: number
    hours?: number
    minutes?: number
  },
) {
  return getDateTime(date).plus(amount).toJSDate()
}

/**
 * Subtracts a specified amount of time (months, days, hours) from a date and returns a new Date object.
 *
 * @param date - The starting date as a Date object or ISO string
 * @param amount - An object specifying the units to subtract (months, days, hours)
 * @returns A new JavaScript Date object with the time subtracted
 *
 * @example
 * ```typescript
 * subtractToDate('2024-01-15', { days: 7 })
 * // Returns: Date object representing 2024-01-08
 *
 * subtractToDate(new Date('2024-03-25'), { months: 2, days: 10 })
 * // Returns: Date object representing 2024-01-15
 * ```
 */
export function subtractToDate(
  date: Date | string,
  amount: {
    months?: number
    weeks?: number
    days?: number
    hours?: number
    minutes?: number
  },
) {
  return getDateTime(date).minus(amount).toJSDate()
}

/**
 * Adjusts a given date or date string based on the provided adjustment settings.
 * Using function overload for extra type-safety
 * If the input value is a string, the function will return a string.
 * If the input value is a Date object, the function will return a Date object.
 *
 * @param {Date | string} value - The date to be adjusted. Can be a Date object or a string.
 * @param {LuxonDateObjectUnits} adjustmentSettings - An object containing the units to adjust the date by.
 * @returns {Date | string} - The adjusted date. The return type matches the type of the input date.
 */
export function adjustDateTime(
  value: string,
  adjustmentSettings: LuxonDateObjectUnits,
): string
export function adjustDateTime(
  value: Date,
  adjustmentSettings: LuxonDateObjectUnits,
): Date
export function adjustDateTime(
  value: Date | string,
  adjustmentSettings: LuxonDateObjectUnits,
): Date | string {
  const paramValue = typeof value === 'string' ? value : value.toISOString()
  const dateTime = LuxonDateTime.fromISO(paramValue, { setZone: true })

  // make the adjustment, keeping the return in the same type format
  const adjusted = dateTime.set(adjustmentSettings)
  return typeof value === 'string'
    ? (adjusted.toISO() ?? '')
    : adjusted.toJSDate()
}

/**
 * Calculates the duration between two dates and returns an object containing the number of full days and remaining hours.
 * @param {Date | string | number} start - The start date, which can be a Date object, a string, or a number.
 * @param {Date | string | number} end - The end date, which can be a Date object, a string, or a number.
 */
export function calculateDurationObject(
  start: Date | string | number,
  end: Date | string | number,
): { days: number; hours: number; minutes: number } {
  const startDate = LuxonDateTime.fromJSDate(new Date(start))
  const endDate = LuxonDateTime.fromJSDate(new Date(end))
  const luxonDuration = endDate.diff(startDate)

  const days = luxonDuration.as('days')
  const hours = luxonDuration.as('hours') % 24 // Get remaining hours after full days
  const minutes = luxonDuration.as('minutes') % 60 // Get remaining minutes after full hours

  return {
    days: Math.floor(days),
    hours: Math.floor(hours),
    minutes: Math.floor(minutes),
  }
}

/**
 * Calculates the age difference between two dates.
 *
 * @param {Date | string | number} startDate - The starting date from which to calculate the age.
 * @param {Date | string | number} [endDate] - The ending date for the calculation.
 * If not provided, the current date and time will be used.
 *
 * @returns {DurationObjectUnits | undefined} An object representing the age difference in years,
 * months, days, hours, minutes, and milliseconds.
 *
 * @example
 * // Calculate age from a specific date to now
 * const age = calculateAge('2000-01-01'); // considering 2024-08-08 as today
 * console.log(age); // { years: 24, months: 7, days: 7, hours: 12, minutes: 30, milliseconds: 500 }
 *
 * @example
 * // Calculate age between two specific dates
 * const age = calculateAge('2000-01-01', '2024-01-01');
 * console.log(age); // { years: 24, months: 0, days: 0, hours: 0, minutes: 0, milliseconds: 0 }
 */
export function calculateAge(
  startDate: Date | string | number,
  endDate?: Date | string | number,
) {
  if (!isDateValid(startDate)) {
    return undefined
  }

  const start = LuxonDateTime.fromISO(new Date(startDate).toISOString())
  const end = endDate
    ? LuxonDateTime.fromISO(new Date(endDate).toISOString())
    : LuxonDateTime.now()

  return end
    .diff(start, [
      'years',
      'months',
      'days',
      'hours',
      'minutes',
      'milliseconds',
    ] satisfies (keyof LuxonDurationObjectUnits)[])
    .toObject()
}

/**
 * Sorts an array of objects containing ISO date strings in descending order (latest first).
 * @param timestamps - Array of objects with a `timestamp` property (ISO string).
 * @returns The sorted array.
 */
export function sortTimestampsDescending<T extends { timestamp: string }>(
  timestamps: T[],
): T[] {
  return timestamps.sort((a, b) => b.timestamp.localeCompare(a.timestamp))
}
